/**
 * Reading a spec while it is still being written.
 *
 * A drawn character takes four minutes, and waiting for the closing brace means
 * four minutes of a progress bar guessing. The JSON arrives in a useful order
 * though: `size` and `kit` first, then `parts` as a list of self-contained
 * objects. So this turns text arriving in pieces into things worth drawing, and
 * the wait becomes the sprite appearing rather than a bar creeping.
 *
 * Deliberately not a JSON parser. It never holds a complete document, and a
 * partial one is a syntax error to anything that expects a whole. It scans for
 * the three structures it knows and ignores everything else, which is also why a
 * shape nobody has written yet still streams: a part is a part.
 *
 * The one rule it must never break is emitting something incomplete. A half
 * written part is not drawn, it is waited for, because a shape missing its role
 * would throw in the renderer and take the whole draw with it.
 */

import type { SpriteSpec } from "./spec.js";

/**
 * The head of the document: everything written before the parts began.
 *
 * `size` and `kit` are what a part needs, and they are not all of it. A spec
 * that turns its outline off, or tiles, or animates, says so up here, and a
 * preview that ignored those would build a sprite that quietly differed from
 * the one that finally arrives. Reported whole for the same reason a drawing's
 * head is: whoever draws it decides what it needs.
 *
 * Sent when the parts start rather than the moment size and kit are both known,
 * because a field written between the kit and the parts would otherwise be
 * missed. Nothing waits on this: the parts begin immediately after.
 */
export interface MetaEvent {
	type: "meta";
	head: Record<string, unknown>;
}
export interface PartEvent {
	type: "part";
	index: number;
	part: Record<string, unknown>;
}
/**
 * The head of a drawing: everything the part said before its `rows` began.
 *
 * A row on its own cannot be painted. It is a line of characters, and what
 * turns those into pixels is `at`, which says where the drawing sits, and
 * `legend`, which says what each character is made of. Those arrive before the
 * rows do, so they are handed over the moment they are complete and the rows
 * that follow have somewhere to go.
 *
 * Emitted with whatever the part had written by then, which is not always
 * enough. A part that writes its legend after its rows produces a head with no
 * legend in it, and nothing can be drawn until the part closes. That is the
 * consumer's call rather than this scanner's: it reports what arrived.
 */
export interface DrawingEvent {
	type: "drawing";
	index: number;
	/** The part's fields so far, without `rows`. */
	head: Record<string, unknown>;
}
export interface RowEvent {
	type: "row";
	/** Which part the row belongs to, since a spec may hold more than one drawing. */
	partIndex: number;
	index: number;
	text: string;
}
export type StreamEvent = MetaEvent | PartEvent | DrawingEvent | RowEvent;

/** Where the scanner is, ignoring anything it is not looking for. */
export class SpecStream {
	private buf = "";
	/** How far the scanner has consumed. Everything before this is finished with. */
	private at = 0;
	private sentMeta = false;
	private partIndex = 0;
	private rowIndex = 0;
	/** Start of the part being accumulated, or -1 between parts. */
	private partStart = -1;
	/** How far into the current part rows have been read. */
	private rowsAt = -1;

	push(text: string): StreamEvent[] {
		this.buf += text;
		const out: StreamEvent[] = [];
		if (!this.sentMeta) this.tryMeta(out);
		this.tryParts(out);
		return out;
	}

	/**
	 * The canvas, the palette and everything else said before the parts.
	 *
	 * Closed off at the `"parts"` key and parsed, the same way a drawing's head
	 * is read: whatever precedes it is already a legal object once a brace is put
	 * on the end. The opening brace is searched for rather than assumed at zero,
	 * because a model sometimes writes a sentence and a fence first.
	 */
	private tryMeta(out: StreamEvent[]): void {
		const p = this.buf.indexOf('"parts"');
		if (p === -1) return;
		const start = this.buf.indexOf("{");
		if (start === -1 || start > p) return;
		const head = this.buf.slice(start, p).replace(/,\s*$/, "");
		let parsed: Record<string, unknown>;
		try {
			parsed = JSON.parse(`${head}}`) as Record<string, unknown>;
		} catch {
			// Not finished arriving, or not a head. Try again on the next chunk.
			return;
		}
		this.sentMeta = true;
		out.push({ type: "meta", head: parsed });
	}

	/**
	 * The index of the character that closes the value starting at `from`.
	 *
	 * Strings are walked rather than counted through, because a brace inside a
	 * glyph id and an escaped quote inside a drawing are both real and both would
	 * close a structure early if the scanner only counted brackets.
	 */
	private balancedEnd(from: number): number {
		const open = this.buf[from];
		if (open !== "{" && open !== "[") {
			// A primitive: run to the next comma or closing bracket at this level.
			for (let i = from; i < this.buf.length; i++) {
				if (/[,}\]]/.test(this.buf[i])) return i - 1;
			}
			return -1;
		}
		const shut = open === "{" ? "}" : "]";
		let depth = 0;
		let inString = false;
		for (let i = from; i < this.buf.length; i++) {
			const c = this.buf[i];
			if (inString) {
				if (c === "\\") i += 1;
				else if (c === '"') inString = false;
				continue;
			}
			if (c === '"') inString = true;
			else if (c === open) depth += 1;
			else if (c === shut) {
				depth -= 1;
				if (depth === 0) return i;
			}
		}
		return -1;
	}

	/**
	 * Parts, and the rows inside a drawing.
	 *
	 * Rows are read from the part still being written, which is the whole point:
	 * an `art` sprite is one part, so waiting for it to close would mean waiting
	 * for the entire drawing and showing nothing until it was done.
	 */
	private tryParts(out: StreamEvent[]): void {
		if (this.at === 0) {
			const p = this.buf.indexOf('"parts"');
			if (p === -1) return;
			const open = this.buf.indexOf("[", p);
			if (open === -1) return;
			this.at = open + 1;
		}

		for (;;) {
			if (this.partStart === -1) {
				const start = this.buf.indexOf("{", this.at);
				if (start === -1) return;
				this.partStart = start;
				this.rowsAt = -1;
				this.rowIndex = 0;
			}

			this.tryRows(out);

			const end = this.balancedEnd(this.partStart);
			if (end === -1) return;
			try {
				const part = JSON.parse(this.buf.slice(this.partStart, end + 1)) as Record<string, unknown>;
				out.push({ type: "part", index: this.partIndex, part });
				this.partIndex += 1;
			} catch {
				// A part that will not parse is skipped rather than thrown: the whole
				// document is validated later, and one unreadable part must not stop
				// the ones behind it from being drawn.
			}
			this.at = end + 1;
			this.partStart = -1;
			this.rowsAt = -1;
		}
	}

	/** Finished strings inside the current part's `rows`, in order, each once. */
	private tryRows(out: StreamEvent[]): void {
		if (this.partStart === -1) return;
		if (this.rowsAt === -1) {
			const r = this.buf.indexOf('"rows"', this.partStart);
			if (r === -1) return;
			const open = this.buf.indexOf("[", r);
			if (open === -1) return;
			this.rowsAt = open + 1;
			this.sendHead(out, r);
		}
		for (;;) {
			const q = this.buf.indexOf('"', this.rowsAt);
			if (q === -1) return;
			// The array ended before another string did: no more rows in this part.
			const close = this.buf.indexOf("]", this.rowsAt);
			if (close !== -1 && close < q) return;
			const end = this.stringEnd(q);
			if (end === -1) return;
			try {
				const text = JSON.parse(this.buf.slice(q, end + 1)) as string;
				out.push({ type: "row", partIndex: this.partIndex, index: this.rowIndex, text });
				this.rowIndex += 1;
			} catch {
				return;
			}
			this.rowsAt = end + 1;
		}
	}

	/**
	 * The part's fields up to where its rows start, closed off and parsed.
	 *
	 * Everything written before `rows` is already a legal object once a brace is
	 * put on the end of it, so the head is read by closing the part early rather
	 * than by a second scanner that would have to be kept in step with the first.
	 * The trailing comma goes with it.
	 */
	private sendHead(out: StreamEvent[], rowsKeyAt: number): void {
		const head = this.buf.slice(this.partStart, rowsKeyAt).replace(/,\s*$/, "");
		try {
			out.push({ type: "drawing", index: this.partIndex, head: JSON.parse(`${head}}`) as Record<string, unknown> });
		} catch {
			// A head that will not parse is not worth reporting. The rows still
			// stream, and the whole part arrives when it closes.
		}
	}

	/** The closing quote of the string opening at `from`, or -1 if it has not arrived. */
	private stringEnd(from: number): number {
		for (let i = from + 1; i < this.buf.length; i++) {
			if (this.buf[i] === "\\") i += 1;
			else if (this.buf[i] === '"') return i;
		}
		return -1;
	}
}

/**
 * What has arrived so far, as something that can be drawn.
 *
 * The scanner reports; this decides. It keeps the head of the document, the
 * parts that have closed, and the rows of the drawing still being written, and
 * hands back a spec of exactly that much sprite. Rendered on each event, the
 * result is the sprite building itself.
 *
 * It lives here rather than in the page because it is the half that is worth
 * testing: every state it passes through has to be a legal sprite, and the last
 * one has to be pixel for pixel the sprite that finally arrives. Neither of
 * those can be asserted about code that only exists inside a browser.
 */
export class SpecBuilder {
	private head: Record<string, unknown> | null = null;
	private parts: Record<string, unknown>[] = [];
	/** The drawing being written, whose rows are still arriving. */
	private drawing: { index: number; head: Record<string, unknown>; rows: string[] } | null = null;

	take(event: StreamEvent): void {
		switch (event.type) {
			case "meta":
				this.head = event.head;
				break;
			case "drawing":
				this.drawing = { index: event.index, head: event.head, rows: [] };
				break;
			case "row":
				// Pushed rather than placed at its index. They arrive in order and
				// each exactly once, and a list with a hole in it is not a drawing:
				// one missing row would be `undefined` where a string has to be.
				if (this.drawing?.index === event.partIndex) this.drawing.rows.push(event.text);
				break;
			case "part":
				this.parts[event.index] = event.part;
				if (this.drawing?.index === event.index) this.drawing = null;
				break;
		}
	}

	/** Start again with nothing, for an attempt that is being drawn afresh. */
	clear(): void {
		this.head = null;
		this.parts = [];
		this.drawing = null;
	}

	/**
	 * The row a drawing has reached, in sprite pixels, or null between drawings.
	 *
	 * Where the printer head is. It is worth showing because it is the one number
	 * in the whole wait that is neither a guess nor a total: it is the line the
	 * model is writing right now.
	 *
	 * Null when the drawing places itself from the far edge, because a negative
	 * `at` is resolved against the canvas by the renderer and this is not the
	 * renderer. A missing line is better than one drawn in the wrong place.
	 */
	get frontier(): number | null {
		if (!this.drawing?.rows.length) return null;
		const at = this.drawing.head.at;
		if (!Array.isArray(at) || typeof at[1] !== "number" || at[1] < 0) return null;
		return at[1] + this.drawing.rows.length;
	}

	/** The canvas to make ready, as soon as it is known. */
	get size(): [number, number] | null {
		const size = this.head?.size;
		return Array.isArray(size) && size.length === 2 ? (size as [number, number]) : null;
	}

	/**
	 * Everything drawable that has arrived, or null while nothing is.
	 *
	 * A drawing part joins in only once it has said where it goes and what its
	 * characters are made of. Without those a row is a line of letters, and
	 * handing it to the renderer would throw and end the show at the first row of
	 * the one sprite that most needs streaming.
	 */
	get spec(): SpriteSpec | null {
		if (!this.size || !this.head?.kit) return null;
		const parts = [...this.parts];
		if (this.drawing && this.drawing.head.at && this.drawing.head.legend) {
			parts[this.drawing.index] = { ...this.drawing.head, rows: this.drawing.rows };
		}
		// A drawing with no rows yet draws nothing and validates as nothing: `rows`
		// must be a non-empty list. It is dropped rather than special-cased, along
		// with any gap left by a part that has not closed.
		const drawable = parts.filter((p) => p && (!Array.isArray(p.rows) || p.rows.length > 0));
		if (!drawable.length) return null;
		// A name is required of a spec and is the one required field that changes
		// no pixels. Supplied when it has not arrived rather than letting the
		// preview refuse to draw over a label nobody sees: it is thrown away with
		// the rest of this object the moment the real spec lands.
		return { name: "drawing", ...this.head, parts: drawable } as unknown as SpriteSpec;
	}
}
